import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Monitor, Ticket, Wifi, BookOpen, Send, Search, ShieldCheck, Download, Copy } from "lucide-react";
import { StatusBadge, SeverityBadge } from "@/components/Primitives";

export default function EmployeePortal() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [deviceState, setDeviceState] = useState({ assigned: false, device: null, message: "" });
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "computer",
    priority: "medium",
    asset_id: "",
  });
  const [q, setQ] = useState("");
  const [installMeta, setInstallMeta] = useState(null);
  const [installLoading, setInstallLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [ticketLoading, setTicketLoading] = useState(false);

  const generateInstallToken = async () => {
    try {
      setInstallLoading(true);
      const { data } = await api.post("/agents/install-token");
      setInstallMeta(data);
      toast.success("Agent install command generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Unable to generate install command");
    } finally {
      setInstallLoading(false);
    }
  };

  const load = async () => {
    const [t, a, d] = await Promise.allSettled([
      api.get("/tickets", { params: { limit: 500 } }),
      api.get("/portal/my-device"),
      api.get("/docs"),
    ]);

    if (t.status === "fulfilled") setTickets(t.value.data);
    if (a.status === "fulfilled") {
      setDeviceState(a.value.data);
      setAgents(a.value.data?.device ? [a.value.data.device] : []);
    }
    if (d.status === "fulfilled") setDocs(d.value.data);
  };

  useEffect(() => { load(); }, []);

  const myAgents = useMemo(() => {
    return deviceState?.device ? [deviceState.device] : [];
  }, [deviceState]);

  const myDevice = deviceState?.device || null;

  const myTickets = useMemo(() => {
    const email = (user?.email || "").toLowerCase();
    const name = (user?.name || "").toLowerCase();

    return tickets.filter(t => {
      const hay = `${t.requester_email || ""} ${t.requester_name || ""} ${t.created_by_email || ""} ${t.created_by_name || ""} ${t.title || ""} ${t.description || ""}`.toLowerCase();
      return hay.includes(email) || hay.includes(name) || t.created_by === user?.id || t.requester_id === user?.id;
    });
  }, [tickets, user]);

  const visibleTickets = myTickets.length ? myTickets : tickets.slice(0, 10);
  const openTickets = visibleTickets.filter(t => t.status !== "closed");
  const inProgress = visibleTickets.filter(t => t.status === "in_progress");
  const waiting = visibleTickets.filter(t => t.status === "pending" || t.status === "waiting_client");
  const resolved = visibleTickets.filter(t => t.status === "closed" || t.status === "resolved");

  const openTicket = async (ticket) => {
    try {
      setSelectedTicket(ticket);
      setTicketLoading(true);
      setTab("ticket-detail");
      const { data } = await api.get(`/portal/tickets/${ticket.id}`);
      setTicketDetail(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Unable to open ticket");
    } finally {
      setTicketLoading(false);
    }
  };

  const sendReply = async () => {
    try {
      if (!selectedTicket?.id) return;
      if (!reply.trim()) return toast.error("Reply is required");

      await api.post(`/portal/tickets/${selectedTicket.id}/comments`, { body: reply });
      setReply("");

      const { data } = await api.get(`/portal/tickets/${selectedTicket.id}`);
      setTicketDetail(data);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Unable to send reply");
    }
  };

  const createTicket = async () => {
    try {
      if (!form.title.trim()) return toast.error("Ticket title is required");
      if (!form.description.trim()) return toast.error("Description is required");

      const payload = {
        ...form,
        requester_id: user?.id,
        requester_name: user?.name,
        requester_email: user?.email,
        asset_id: form.asset_id || myDevice?.asset_id || "",
      };

      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket #${data.number || ""} created`);

      setForm({
        title: "",
        description: "",
        category: "computer",
        priority: "medium",
        asset_id: "",
      });

      setTab("tickets");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Unable to create ticket");
    }
  };

  const kb = docs.filter(d => {
    const hay = `${d.title || ""} ${d.category || ""} ${d.content || ""}`.toLowerCase();
    return !q || hay.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">End User Portal</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Hi, {user?.name || "User"}</h1>
        <p className="text-sm text-zinc-500 mt-1">Check your device, submit support tickets, and find help articles.</p>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <Card icon={Monitor} label="My Device" value={myDevice?.hostname || "No device assigned"} sub={myDevice?.status || "Install agent"} onClick={() => setTab("device")} />
        <Card icon={Ticket} label="Open Tickets" value={openTickets.length} sub="Active requests" onClick={() => setTab("tickets")} />
        <Card icon={Wifi} label="Internet Status" value={myDevice?.status === "online" ? "Connected" : "Unknown"} sub={myDevice?.metrics?.ip_address || "No IP"} onClick={() => setTab("device")} />
        <Card icon={ShieldCheck} label="Protection" value={isProtected(myDevice) ? "Protected" : "Needs Check"} sub="Defender / Firewall" onClick={() => setTab("device")} />
        <Card icon={Download} label="Agent Installer" value={myDevice ? "Installed" : "Download"} sub={myDevice ? "Device linked" : "Protect this device"} onClick={() => setTab("agent")} />
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-900">
        {[
          ["dashboard", "Dashboard"],
          ["tickets", "My Tickets"],
          ["ticket-detail", selectedTicket ? `Ticket #${selectedTicket.number || ""}` : "Ticket Detail"],
          ["new", "Request Support"],
          ["device", "My Device"],
          ["agent", "Download Agent"],
          ["kb", "Knowledge Base"],
        ].filter(([k]) => k !== "ticket-detail" || selectedTicket).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm border-b-2 ${tab === k ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Panel title="My Device Status">
            {!myDevice && <div className="p-5 text-sm text-amber-300 border-b border-zinc-900">{deviceState?.message || "No device assigned yet."}</div>}
            <Info label="Hostname" value={myDevice?.hostname} />
            <Info label="Status" value={myDevice?.status} />
            <Info label="Current User" value={myDevice?.metrics?.current_user} />
            <Info label="Last Seen" value={myDevice?.last_heartbeat?.slice(0, 19)?.replace("T", " ")} />
          </Panel>

          <Panel title="My Tickets">
            <Info label="Open" value={openTickets.length} />
            <Info label="In Progress" value={inProgress.length} />
            <Info label="Waiting for Me" value={waiting.length} />
            <Info label="Resolved" value={resolved.length} />
          </Panel>

          <Panel title="Need Help?">
            <button onClick={() => setTab("new")} className="w-full bg-blue-600 hover:bg-blue-700 rounded px-4 py-3 text-sm text-white flex items-center justify-center gap-2">
              <Send size={15} /> Request IT Support
            </button>
            <button onClick={() => setTab("agent")} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 rounded px-4 py-3 text-sm text-white flex items-center justify-center gap-2">
              <Download size={15} /> Download Agent
            </button>
            <button onClick={() => setTab("kb")} className="w-full mt-3 bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-4 py-3 text-sm text-zinc-200">
              Search Knowledge Base
            </button>
          </Panel>
        </div>
      )}

      {tab === "tickets" && (
        <Panel title={`My Tickets (${visibleTickets.length})`}>
          <TicketTable tickets={visibleTickets} onOpen={openTicket} />
        </Panel>
      )}

      {tab === "ticket-detail" && (
        <Panel title={ticketDetail?.ticket ? `Ticket #${ticketDetail.ticket.number}: ${ticketDetail.ticket.title}` : "Ticket Detail"}>
          {ticketLoading ? (
            <div className="p-8 text-sm text-zinc-500">Loading ticket…</div>
          ) : ticketDetail?.ticket ? (
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Info label="Status" value={ticketDetail.ticket.status} />
                <Info label="Priority" value={ticketDetail.ticket.priority} />
                <Info label="Category" value={ticketDetail.ticket.category} />
                <Info label="Created" value={ticketDetail.ticket.created_at?.slice(0, 16)?.replace("T", " ")} />
              </div>

              <div className="bg-zinc-900/40 border border-zinc-800 rounded-md p-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Description</div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap">{ticketDetail.ticket.description || "No description."}</div>
              </div>

              <div className="border border-zinc-900 rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-900 font-medium">Conversation</div>
                {(ticketDetail.comments || []).length === 0 ? (
                  <div className="p-5 text-sm text-zinc-500">No replies yet.</div>
                ) : (
                  <div>
                    {(ticketDetail.comments || []).map(c => (
                      <div key={c.id} className="p-4 border-b border-zinc-900">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-medium">
                            {c.actor_name || c.author_name || c.actor_email || c.author_email || "Support"}
                          </div>
                          <div className="text-xs font-mono text-zinc-500">
                            {(c.at || c.created_at)?.slice(0, 16)?.replace("T", " ")}
                          </div>
                        </div>
                        <div className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{c.body || c.comment || ""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-zinc-900 rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-900 font-medium">Reply to IT</div>
                <div className="p-4 space-y-3">
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    rows={4}
                    className="input"
                    placeholder="Type your reply or update here..."
                  />
                  <div className="flex justify-end">
                    <button onClick={sendReply} className="bg-blue-600 hover:bg-blue-700 rounded px-5 py-2 text-sm text-white">
                      Send Reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-sm text-zinc-500">Ticket not found.</div>
          )}
        </Panel>
      )}

      {tab === "new" && (
        <Panel title="Request IT Support">
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Problem Type">
              <select value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="input">
                <option value="computer">Computer</option>
                <option value="internet">Internet</option>
                <option value="printer">Printer</option>
                <option value="email">Email</option>
                <option value="software">Software</option>
                <option value="access_request">Access Request</option>
                <option value="security">Security</option>
                <option value="other">Other</option>
              </select>
            </Field>

            <Field label="Priority">
              <select value={form.priority} onChange={e=>setForm({...form, priority:e.target.value})} className="input">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </Field>

            <Field label="Affected Device">
              <select value={form.asset_id} onChange={e=>setForm({...form, asset_id:e.target.value})} className="input">
                <option value="">Use my detected device</option>
                {myAgents.map(a => <option key={a.id} value={a.asset_id || a.id}>{a.hostname || a.id}</option>)}
              </select>
            </Field>

            <Field label="Title">
              <input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} className="input" placeholder="Example: Internet keeps disconnecting" />
            </Field>

            <div className="md:col-span-2">
              <Field label="Description">
                <textarea value={form.description} onChange={e=>setForm({...form, description:e.target.value})} rows={5} className="input" placeholder="Tell us what happened, when it started, and whether it affects your work." />
              </Field>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <button onClick={createTicket} className="bg-blue-600 hover:bg-blue-700 rounded px-5 py-2 text-sm text-white">
                Create Support Ticket
              </button>
            </div>
          </div>
        </Panel>
      )}

      {tab === "device" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel title="My Device">
            <Info label="Hostname" value={myDevice?.hostname} />
            <Info label="Status" value={myDevice?.status} />
            <Info label="OS" value={myDevice?.os || myDevice?.metrics?.os} />
            <Info label="Current User" value={myDevice?.metrics?.current_user} />
            <Info label="IP Address" value={myDevice?.metrics?.ip_address} />
            <Info label="Agent Version" value={myDevice?.metrics?.agent_version || myDevice?.version} />
          </Panel>

          <Panel title="Device Health">
            <Info label="CPU" value={pct(myDevice?.metrics?.cpu)} />
            <Info label="RAM" value={pct(myDevice?.metrics?.mem)} />
            <Info label="Disk" value={pct(myDevice?.metrics?.disk)} />
            <Info label="Uptime" value={formatUptime(myDevice?.metrics?.uptime_sec)} />
            <Info label="Internet Stability" value="Basic online/offline enabled. Disconnect history comes next." />
          </Panel>
        </div>
      )}

      {tab === "agent" && (
        <Panel title="Download APOCALYNX Agent">
          <div className="p-5 space-y-5">
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-md p-4">
              <div className="font-medium">Install the APOCALYNX Agent on this computer</div>
              <p className="text-sm text-zinc-500 mt-2">
                This connects your device to support so IT can monitor device health, help troubleshoot issues, and respond faster to tickets.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a
                href="/downloads/APOCALYNXAgent.exe"
                className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-4 py-3 text-sm text-zinc-200 text-center flex items-center justify-center gap-2"
              >
                <Download size={15} /> Download Windows Agent
              </a>

              <a
                href="/downloads/install-apocalynx-agent.ps1"
                className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-4 py-3 text-sm text-zinc-200 text-center flex items-center justify-center gap-2"
              >
                <Download size={15} /> Download Installer Script
              </a>
            </div>

            <div className="border border-zinc-900 rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
                <div>
                  <div className="font-medium">One-Line Install Command</div>
                  <div className="text-xs text-zinc-500 mt-1">Run this in PowerShell as Administrator.</div>
                </div>
                <button
                  onClick={generateInstallToken}
                  disabled={installLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 rounded px-3 py-2 text-sm text-white"
                >
                  {installLoading ? "Generating..." : "Generate Command"}
                </button>
              </div>

              {installMeta ? (
                <div className="p-4 space-y-3">
                  <code className="block bg-black border border-zinc-900 rounded p-3 text-xs font-mono text-zinc-300 break-all whitespace-pre-wrap">
                    iwr https://rmm.lynxclub.ph/downloads/install-apocalynx-agent.ps1 -OutFile $env:TEMP\install-apocalynx-agent.ps1; powershell -ExecutionPolicy Bypass -File $env:TEMP\install-apocalynx-agent.ps1 -InstallToken "{installMeta.install_token}"
                  </code>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`iwr https://rmm.lynxclub.ph/downloads/install-apocalynx-agent.ps1 -OutFile $env:TEMP\\install-apocalynx-agent.ps1; powershell -ExecutionPolicy Bypass -File $env:TEMP\\install-apocalynx-agent.ps1 -InstallToken "${installMeta.install_token}"`);
                      toast.success("Install command copied");
                    }}
                    className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-4 py-2 text-sm text-zinc-200 flex items-center gap-2"
                  >
                    <Copy size={14} /> Copy Install Command
                  </button>
                </div>
              ) : (
                <div className="p-4 text-sm text-zinc-500">
                  Click Generate Command to create a secure install command for this device.
                </div>
              )}
            </div>

            <div className="bg-amber-950/20 border border-amber-900/50 rounded p-4 text-sm text-amber-200">
              If Windows asks for permission, choose <b>Yes</b>. If you do not have administrator access, contact IT support.
            </div>
          </div>
        </Panel>
      )}

      {tab === "kb" && (
        <Panel title="Knowledge Base">
          <div className="p-5 border-b border-zinc-900">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search help articles..." className="input pl-9" />
            </div>
          </div>
          <div>
            {kb.map((d, idx) => (
              <div key={d.id || idx} className="p-5 border-b border-zinc-900">
                <div className="font-medium">{d.title || "Untitled Article"}</div>
                <div className="text-xs text-zinc-500 mt-1">{d.category || "General"}</div>
                <p className="text-sm text-zinc-400 mt-3 line-clamp-3">{d.content || d.body || "No article preview available."}</p>
              </div>
            ))}
            {kb.length === 0 && <div className="p-8 text-sm text-zinc-500">No knowledge base articles found.</div>}
          </div>
        </Panel>
      )}

      <style>{`
        .input {
          width: 100%;
          background: rgb(24 24 27);
          border: 1px solid rgb(39 39 42);
          border-radius: 0.375rem;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
        }
        .input:focus {
          border-color: rgba(59, 130, 246, 0.7);
        }
      `}</style>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, onClick }) {
  return (
    <button onClick={onClick} className="bg-zinc-950 border border-zinc-900 hover:border-blue-700 rounded-md p-4 text-left">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
        <Icon size={15} className="text-blue-400" />
      </div>
      <div className="font-mono text-xl mt-4 truncate">{value || "—"}</div>
      <div className="text-xs text-zinc-500 mt-1 truncate">{sub || "—"}</div>
    </button>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">{title}</div>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
      <span className="text-sm text-zinc-500">{label}</span>
      <span className="text-sm font-mono text-zinc-200 text-right">{value || "—"}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      {children}
    </label>
  );
}

function TicketTable({ tickets, onOpen }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr>
          <th className="text-left px-4 py-3">#</th>
          <th className="text-left px-4 py-3">Title</th>
          <th className="text-left px-4 py-3">Priority</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-left px-4 py-3">SLA</th>
        </tr>
      </thead>
      <tbody>
        {tickets.map(t => (
          <tr key={t.id} className="border-b border-zinc-900">
            <td className="px-4 py-2 font-mono text-zinc-500">#{t.number}</td>
            <td className="px-4 py-2">
              <button onClick={() => onOpen(t)} className="text-blue-300 hover:text-blue-200 hover:underline text-left">
                {t.title}
              </button>
            </td>
            <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
            <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{t.sla_deadline?.slice(0,16)?.replace("T"," ") || "—"}</td>
          </tr>
        ))}
        {tickets.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-sm text-zinc-500">No tickets found.</td></tr>}
      </tbody>
    </table>
  );
}

function pct(v) {
  if (v === undefined || v === null) return "—";
  return `${v}%`;
}

function formatUptime(sec) {
  if (!sec) return "—";
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  return `${days}d ${hours}h`;
}

function isProtected(agent) {
  const sec = agent?.metrics?.security;
  if (!sec) return false;
  const defender = sec.defender || {};
  const firewall = sec.firewall || [];
  const defenderOk = defender.AntivirusEnabled !== false && defender.RealTimeProtectionEnabled !== false;
  const firewallOk = firewall.length === 0 || firewall.some(f => f.Enabled === true);
  return defenderOk && firewallOk;
}
